import { cleanupWhatsappSession } from 'dashboard/composables/useWhatsappCallSession';
import { TERMINAL_STATUSES } from 'dashboard/helper/voice';
import { defineStore } from 'pinia';

const teardownCall = () => cleanupWhatsappSession();

export const useCallsStore = defineStore('calls', {
  state: () => ({
    calls: [],
    acceptingCallSid: null,
    settledCallSids: [],
  }),

  getters: {
    activeCall: state => state.calls.find(call => call.isActive) || null,
    hasActiveCall: state => state.calls.some(call => call.isActive),
    incomingCalls: state => state.calls.filter(call => !call.isActive),
    hasIncomingCall: state => state.calls.some(call => !call.isActive),
  },

  actions: {
    handleCallStatusChanged({ callSid, status }) {
      if (!TERMINAL_STATUSES.includes(status)) return;
      this.settledCallSids = [
        ...new Set([...this.settledCallSids, callSid]),
      ].slice(-200);

      const call = this.calls.find(c => c.callSid === callSid);
      // WhatsApp recordings live in the in-memory recorder until voice_call.ended
      // uploads them; tearing down here would race-wipe those chunks.
      if (call?.provider === 'whatsapp') {
        this.calls = this.calls.filter(c => c.callSid !== callSid);
        return;
      }

      this.removeCall(callSid);
    },

    addCall(callData) {
      if (!callData?.callSid) return;
      if (this.settledCallSids.includes(callData.callSid)) return;
      const existing = this.calls.find(c => c.callSid === callData.callSid);
      if (existing) {
        // Merge so a later cable event with sdp_offer/provider/caller fills in
        // gaps left by the earlier message.created path (and vice versa).
        // Preserve a previously-captured caller snapshot when the incoming
        // event has no sender info, otherwise the widget would flip to
        // "Unknown caller" on the next status update.
        const next = { ...callData };
        if (existing.caller && !next.caller) delete next.caller;
        Object.assign(existing, next, { isActive: existing.isActive });
        return;
      }

      // Prepend so the newest call surfaces as the primary card (incomingCalls[0])
      // and older calls drop into the stack below it.
      this.calls.unshift({
        ...callData,
        isActive: false,
      });
    },

    addHandoffCall(callData) {
      this.settledCallSids = this.settledCallSids.filter(
        sid => sid !== callData?.callSid
      );
      this.addCall(callData);
    },

    removeCall(callSid) {
      const callToRemove = this.calls.find(c => c.callSid === callSid);
      if (callToRemove?.isActive) teardownCall();
      this.calls = this.calls.filter(c => c.callSid !== callSid);
    },

    setCallActive(callSid) {
      this.calls = this.calls.map(call => ({
        ...call,
        isActive: call.callSid === callSid,
      }));
    },

    markCallAccepting(callSid) {
      this.acceptingCallSid = callSid;
    },

    clearCallAccepting(callSid) {
      if (this.acceptingCallSid === callSid) this.acceptingCallSid = null;
    },

    handleCallAccepted(callSid) {
      if (!callSid) return;
      this.settledCallSids = [
        ...new Set([...this.settledCallSids, callSid]),
      ].slice(-200);
      const call = this.calls.find(item => item.callSid === callSid);
      // Keep the call in the tab that is accepting it, and in a tab where it
      // has already become active. All other agents' copies are stale rings.
      if (this.acceptingCallSid !== callSid && !call?.isActive)
        this.removeCall(callSid);
    },

    handleCallAcceptedByLumerai(callSid) {
      if (!callSid) return;
      this.settledCallSids = [
        ...new Set([...this.settledCallSids, callSid]),
      ].slice(-200);
      // The AI accepted the call on the server. No browser should keep a
      // ringing card (including a tab that had already started its accept).
      this.removeCall(callSid);
    },

    clearActiveCall() {
      const active = this.calls.find(c => c.isActive);
      if (active) teardownCall();
      this.calls = this.calls.filter(call => !call.isActive);
    },

    dismissCall(callSid) {
      this.calls = this.calls.filter(call => call.callSid !== callSid);
    },

    removeCallsForConversation(conversationId) {
      const callsToRemove = this.calls.filter(
        call => call.conversationId === conversationId
      );

      callsToRemove.filter(call => call.isActive).forEach(teardownCall);

      this.calls = this.calls.filter(
        call => call.conversationId !== conversationId
      );
    },

    removeIncomingCallsForConversation(conversationId) {
      const incomingCalls = this.calls.filter(
        call => call.conversationId === conversationId && !call.isActive
      );
      const settledIds = incomingCalls
        .map(call => call.callSid)
        .filter(Boolean);
      this.settledCallSids = [
        ...new Set([...this.settledCallSids, ...settledIds]),
      ].slice(-200);
      this.calls = this.calls.filter(
        call => call.conversationId !== conversationId || call.isActive
      );
    },
  },
});
